import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Delete from '@mui/icons-material/Delete';
import Download from '@mui/icons-material/Download';
import Edit from '@mui/icons-material/Edit';
import Email from '@mui/icons-material/Email';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import Sync from '@mui/icons-material/Sync';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography, { TypographyOwnProps } from '@mui/material/Typography';
import {
  DeliveredQuantity_Read,
  InvoiceCategoryFilter,
  ReconcileState,
  SettlementExportStatus,
  SettlementState,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import React from 'react';

import actionsColumn from '~components/DataTable/actionsColumn';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import LinkableText from '~components/LinkableText/LinkableText';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Settlement } from '~hooks/useSettlements/models';
import { dateFormat } from '~utils/dateTime';
import {
  getFriendlySettlementId,
  getSettlementFor,
} from '~utils/settlements/settlement-utils';
import { numberToIntegerOrDecimal } from '~utils/utilFunctions';

import SettlementStatus from '../components/SettlementStatus';
import {
  buildFilesSearchUrl,
  canExportToAccounting,
  canRebillSettlement,
  canSendEmail,
  canVoidSettlement,
} from './helper';
import RutterSettlementExportStatus from './SettlementDetails/RutterSettlementExportStatus';
import SettlementExportBatchStatus from './SettlementDetails/SettlementExportBatchStatus';
import SettlementOutboundEmailStatus from './SettlementDetails/SettlementOutboundEmailStatus';

const CellText = styled(Typography)<TypographyOwnProps>(() => ({
  '&.MuiTypography-root': {
    fontSize: '12px',
  },
}));

const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

type CurrencyFormatterFn = ReturnType<typeof useCompanyCurrency>['currencyFormatter'];

type SettlementsRowDefUtils = {
  currencyFormatter: CurrencyFormatterFn;
  onEditButtonClick: (settlement: Settlement) => void;
  onExportToPDFButtonClick: (settlement: Settlement) => void;
  onEmailSettlementButtonClick: (settlement: Settlement) => void;
  onVoidSettlementButtonClick: (settlement: Settlement) => void;
  onVoidRebillSettlementButtonClick: (settlement: Settlement) => void;
  onExportToAccountingButtonClick: (settlement: Settlement) => void;
  onDownloadFilesButtonClick: (settlement: Settlement) => void;
  onSettlementChange: (settlement: Settlement) => void;
};

export type SettlementsRow = {
  id: string;
  isGeneratingPdfForSettlementId?: string;
  settlement: Settlement;
  category?: InvoiceCategoryFilter;
  utils: SettlementsRowDefUtils;
};

type SettlementsColumn = DataTableColumn<SettlementsRow>;

export const createdDateColDef: SettlementsColumn = {
  field: 'createdDate',
  headerName: t('common.created_date'),
  renderCell: (row) => {
    return (
      <CellText>{dateFormat(row.settlement.createdAt, 'hh:mm A MM/DD/YY')}</CellText>
    );
  },
};

export const dueDateColDef: SettlementsColumn = {
  field: 'dueDate',
  headerName: t('form_fields.due_date'),
  renderCell: (row) => {
    return (
      <CellText>
        {row.settlement.dueDate ? dateFormat(row.settlement.dueDate, 'MM/DD/YY') : ''}
      </CellText>
    );
  },
};

export const settlementIdColDef: SettlementsColumn = {
  field: 'settlementId',
  headerName: t('approvals.driver_pay.settlements.settlement_id'),
  renderCell: (row) => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <CellText>{getFriendlySettlementId(row.settlement, row.category)}</CellText>
        {row.settlement.originalSettlement && (
          <CellText>
            {`${t('approvals.driver_pay.settlements.rebill_from', {
              settlementId: getFriendlySettlementId(
                row.settlement.originalSettlement,
                row.category,
              ),
            })}`}
          </CellText>
        )}
      </Box>
    );
  },
};

export const forColDef: SettlementsColumn = {
  field: 'for',
  headerName: t('approvals.driver_pay.settlements.for'),
  renderCell: (row) => {
    const settlementFor = getSettlementFor(row.settlement, row.category);
    return <CellText>{settlementFor.name}</CellText>;
  },
};

export const orderIdsColDef: SettlementsColumn = {
  field: 'orderIds',
  headerName: t('approvals.driver_pay.settlements.order_ids'),
  renderCell: (row) => {
    const friendlyOrderIds = [...(row.settlement.data.friendlyOrderIds ?? [])].sort();

    if (friendlyOrderIds.length === 0) {
      return <CellText>-</CellText>;
    }

    return (
      <Tooltip title={friendlyOrderIds.join(', ')}>
        <Box
          onClick={stopRowClick}
          sx={{
            fontSize: '12px',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {friendlyOrderIds.map((orderId, index) => (
            <React.Fragment key={orderId}>
              <LinkableText
                component="span"
                variant="body2"
                sx={{ fontSize: '12px' }}
                href={buildFilesSearchUrl(orderId)}
              >
                {orderId}
              </LinkableText>
              {index < friendlyOrderIds.length - 1 && ', '}
            </React.Fragment>
          ))}
        </Box>
      </Tooltip>
    );
  },
};

export const jobsColDef: SettlementsColumn = {
  field: 'jobs',
  headerName: t('form_fields.jobs'),
  renderCell: (row) => {
    return <CellText>{row.settlement.jobsCount}</CellText>;
  },
};

export const formatDeliveredQuantities = (
  deliveredQuantities: DeliveredQuantity_Read[],
): string[] => {
  const groupedByUom = deliveredQuantities.reduce(
    (acc, dq) => {
      const uom = dq.unit_of_measure || '';
      const delivered = parseFloat(dq.delivered) || 0;
      acc[uom] = (acc[uom] || 0) + delivered;
      return acc;
    },
    {} as Record<string, number>,
  );

  return Object.entries(groupedByUom)
    .filter(([, total]) => total > 0)
    .map(([uom, total]) => `${numberToIntegerOrDecimal(total)} ${uom}`.trim())
    .filter((str) => str.length > 0);
};

export const qtysAndUomsColDef: SettlementsColumn = {
  field: 'qtysAndUoms',
  headerName: t('approvals.driver_pay.settlements.qtys_and_uoms'),
  renderCell: (row) => {
    return (
      <CellText>
        {formatDeliveredQuantities(row.settlement.deliveredQuantities).join(', ')}
      </CellText>
    );
  },
};

export const totalColDef: SettlementsColumn = {
  field: 'total',
  headerName: t('common.total'),
  renderCell: (row) => {
    const totalAmount = row.settlement.rebillTotalAmount ?? row.settlement.totalAmount;
    return (
      <CellText>
        {totalAmount >= 0
          ? row.utils.currencyFormatter(totalAmount)
          : `(${row.utils.currencyFormatter(-totalAmount)})`}
      </CellText>
    );
  },
};

export const stateColDef: SettlementsColumn = {
  minWidth: 125,
  field: 'state',
  headerName: t('form_fields.status'),
  renderCell: (row) => {
    return (
      <Box onClick={stopRowClick}>
        <SettlementStatus
          onSettlementChange={row.utils.onSettlementChange}
          settlement={row.settlement}
        />
      </Box>
    );
  },
};

const reconcileStateInfo: Record<
  ReconcileState,
  { label: string; icon: React.ReactNode }
> = {
  [ReconcileState.IN_PROGRESS]: {
    label: t('approvals.driver_pay.settlements.reconcile_state.in_progress'),
    icon: <HourglassEmpty color="warning" fontSize="small" sx={{ mr: 0.5 }} />,
  },
  [ReconcileState.COMPLETED]: {
    label: t('approvals.driver_pay.settlements.reconcile_state.completed'),
    icon: <CheckCircleOutline color="success" fontSize="small" sx={{ mr: 0.5 }} />,
  },
  [ReconcileState.FAILED]: {
    label: t('approvals.driver_pay.settlements.reconcile_state.failed'),
    icon: <WarningAmber color="error" fontSize="small" sx={{ mr: 0.5 }} />,
  },
};

export const reconcileStateColDef: SettlementsColumn = {
  field: 'reconcileState',
  headerName: t('approvals.driver_pay.settlements.reconcile_state.label'),
  renderCell: (row) => {
    const state = row.settlement.reconcileState;

    if (!state) {
      return <CellText>-</CellText>;
    }

    const { icon, label } = reconcileStateInfo[state];

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <CellText>{label}</CellText>
      </Box>
    );
  },
};

export const exportedColDef: SettlementsColumn = {
  minWidth: 190,
  field: 'exported',
  headerName: t('approvals.driver_pay.tabs.exported'),
  renderCell: (row) => {
    const hasRutterExport = row.settlement.rutterSettlementExport;
    const lastSettlementExport = row.settlement.lastSettlementExport;
    // A completed batch also stamps `exported_at`, so the batch line already covers it.
    const hasCompletedSettlementExportBatch =
      lastSettlementExport?.status === SettlementExportStatus.COMPLETED;
    const hasManualExport =
      !hasRutterExport && !hasCompletedSettlementExportBatch && row.settlement.exportedAt;
    const lastOutboundEmail = row.settlement.lastOutboundEmail;

    const hasNoExportInfo =
      !hasRutterExport && !lastSettlementExport && !hasManualExport && !lastOutboundEmail;

    if (hasNoExportInfo) {
      return <CellText>-</CellText>;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {hasRutterExport && (
          <RutterSettlementExportStatus
            rutterSettlementExport={hasRutterExport}
            sx={{ fontSize: '12px' }}
            labelSx={{ fontSize: '12px' }}
          />
        )}
        {lastSettlementExport && (
          <SettlementExportBatchStatus
            settlementExport={lastSettlementExport}
            sx={{ fontSize: '12px' }}
            labelSx={{ fontSize: '12px' }}
          />
        )}
        {hasManualExport && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '12px',
            }}
          >
            <CheckCircleOutline color="success" sx={{ mr: 1 }} fontSize="small" />
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
              {t('approvals.driver_pay.tabs.exported')}
              {' • '}
              {dayjs.tz(row.settlement.exportedAt).format('MMM DD')}
            </Typography>
          </Box>
        )}
        {lastOutboundEmail && (
          <SettlementOutboundEmailStatus
            outboundEmail={lastOutboundEmail}
            sx={{ fontSize: '12px' }}
            labelSx={{ fontSize: '12px' }}
          />
        )}
      </Box>
    );
  },
};

export const actionsColDef = (
  isAccountingExportEnabled: boolean,
  isChargeBasedSettlementsEnabled: boolean,
): SettlementsColumn =>
  actionsColumn((row) => {
    const canSendSettlement = canSendEmail(row.settlement);
    const canExport = canExportToAccounting(row.settlement, isAccountingExportEnabled);
    const canVoid = canVoidSettlement(row.settlement);
    const canRebill = canRebillSettlement(row.settlement);
    const canEdit =
      !isChargeBasedSettlementsEnabled || row.settlement.state !== SettlementState.VOIDED;
    const isReceivablesCategory = row.category === InvoiceCategoryFilter.RECEIVABLES;

    const actions = [
      {
        callBack: () => row.utils.onExportToPDFButtonClick(row.settlement),
        icon: <PictureAsPdf />,
        title: t('approvals.driver_pay.settlements.generate_pdf'),
      },
      {
        callBack: () => row.utils.onDownloadFilesButtonClick(row.settlement),
        icon: <Download />,
        title: t('approvals.driver_pay.settlements.download_files'),
      },
    ];

    if (canVoid) {
      actions.push({
        callBack: () => row.utils.onVoidSettlementButtonClick(row.settlement),
        icon: <Delete />,
        title: t('actions.void'),
      });
    }

    if ((canVoid || canRebill) && isReceivablesCategory) {
      actions.push({
        callBack: () => row.utils.onVoidRebillSettlementButtonClick(row.settlement),
        icon: <Delete />,
        title: t('approvals.driver_pay.settlements.void_settlement.void_rebill'),
      });
    }

    const isGeneratingPdf = !isNil(row.isGeneratingPdfForSettlementId);
    const isGeneratingPdfForThisSettlement =
      row.isGeneratingPdfForSettlementId &&
      row.isGeneratingPdfForSettlementId === String(row.settlement.id);

    return (
      <Box
        onClick={stopRowClick}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {canExport && (
          <SmallButton
            onClick={() => row.utils.onExportToAccountingButtonClick(row.settlement)}
            variant="outlined"
            color="secondary"
          >
            {t('approvals.driver_pay.settlements.export_to_accounting.export_button')}
          </SmallButton>
        )}
        {canSendSettlement && (
          <SmallButton
            onClick={() => row.utils.onEmailSettlementButtonClick(row.settlement)}
            variant="outlined"
            color="secondary"
            startIcon={<Email />}
          >
            {t('approvals.driver_pay.settlements.email_settlement.email_cta')}
          </SmallButton>
        )}
        {canEdit && (
          <IconButton
            aria-label={t('actions.edit')}
            onClick={() => row.utils.onEditButtonClick(row.settlement)}
            size="small"
          >
            <Edit />
          </IconButton>
        )}
        <SimpleMenu options={actions} disabled={isGeneratingPdf}>
          {isGeneratingPdfForThisSettlement ? (
            <Sync
              sx={{
                '@keyframes generate-pdf-spinner': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(-360deg)' },
                },
                animation: 'generate-pdf-spinner 2s linear infinite',
              }}
            />
          ) : (
            <MoreHoriz />
          )}
        </SimpleMenu>
      </Box>
    );
  });
